import {
  assertResponseBelongsToAuthSession,
  captureAuthSessionSnapshot,
  ensureAuthSessionBoundarySync,
  getSessionAuthFailure,
  type AuthSessionSnapshot,
} from '@/api/session-auth-barrier';
import { onExternalWebAdminAuthBoundary, onWebAdminAuthError } from '@/lib/auth-events';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/store/auth';

let unsubscribeWebAdminAuthErrorBridge: (() => void) | null = null;
let authTransitionRecoveryTimeout: ReturnType<typeof setTimeout> | null = null;

const AUTH_TRANSITION_RECOVERY_DELAY_MS = 2_000;

function cancelAuthTransitionRecovery(): void {
  if (authTransitionRecoveryTimeout !== null) {
    clearTimeout(authTransitionRecoveryTimeout);
    authTransitionRecoveryTimeout = null;
  }
}

function scheduleAuthTransitionRecovery(transitionSnapshot: AuthSessionSnapshot): void {
  cancelAuthTransitionRecovery();
  authTransitionRecoveryTimeout = setTimeout(() => {
    authTransitionRecoveryTimeout = null;
    try {
      // A later transition/login/logout owns its own recovery decision.
      assertResponseBelongsToAuthSession(transitionSnapshot);
    } catch {
      return;
    }
    if (getSessionAuthFailure()?.error !== 'auth_session_transition') {
      return;
    }

    // /auth/me enters the same shared auth lock as login/logout. If the
    // initiator is merely slow, this waits behind it; if the tab crashed, the
    // released lock lets the authoritative cookie reopen or close the session.
    void useAuthStore.getState().checkAuth({ retryOnTransient: true });
  }, AUTH_TRANSITION_RECOVERY_DELAY_MS);
}

export function ensureWebAdminAuthErrorBridge(): void {
  if (unsubscribeWebAdminAuthErrorBridge || typeof window === 'undefined') {
    return;
  }

  const unsubscribeAuthError = onWebAdminAuthError(() => {
    cancelAuthTransitionRecovery();
    const authStore = useAuthStore.getState();
    authStore.clearAuth({ preserveSessionHint: true });
    // Fail closed first, then ask the cookie-authoritative endpoint to resolve
    // races that cannot be ordered through durable storage (private/blocked
    // localStorage, delayed cross-tab delivery, or a committed cookie followed
    // by a transport failure). Genuine expiry remains unauthenticated.
    void authStore.checkAuth({ retryOnTransient: true });
  });
  const unsubscribeExternalAuthBoundary = onExternalWebAdminAuthBoundary((kind) => {
    const authStore = useAuthStore.getState();
    authStore.clearAuth({ preserveSessionHint: true });
    if (kind === 'transition') {
      scheduleAuthTransitionRecovery(captureAuthSessionSnapshot());
      return;
    }

    cancelAuthTransitionRecovery();
    // Login resolves the new principal; logout is also reconciled so a stale
    // degraded-mode boundary cannot leave a valid newer cookie logged out.
    void authStore.checkAuth({ retryOnTransient: true });
  });
  const unsubscribeAuthState = useAuthStore.subscribe((state, previousState) => {
    const enteredUnauthenticated =
      state.status === 'unauthenticated' && previousState.status !== 'unauthenticated';
    const enteredAuthenticated =
      state.status === 'authenticated' && previousState.status !== 'authenticated';
    const authenticatedPrincipalChanged =
      state.status === 'authenticated' &&
      previousState.status === 'authenticated' &&
      state.user?.id !== previousState.user?.id;

    if (enteredUnauthenticated || enteredAuthenticated || authenticatedPrincipalChanged) {
      // Tenant/user-scoped server state must never cross an auth boundary.
      // Clear before a newly authenticated principal enters protected routes.
      queryClient.clear();
    }
  });
  // Install consumers before synchronizing the durable cross-tab epoch. A tab
  // opened after an existing login/logout boundary must observe that first
  // reconciliation event instead of dropping it during bridge startup.
  ensureAuthSessionBoundarySync();
  unsubscribeWebAdminAuthErrorBridge = () => {
    cancelAuthTransitionRecovery();
    unsubscribeAuthError();
    unsubscribeExternalAuthBoundary();
    unsubscribeAuthState();
  };
}

export function __resetWebAdminAuthErrorBridgeForTests(): void {
  cancelAuthTransitionRecovery();
  unsubscribeWebAdminAuthErrorBridge?.();
  unsubscribeWebAdminAuthErrorBridge = null;
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cancelAuthTransitionRecovery();
    unsubscribeWebAdminAuthErrorBridge?.();
    unsubscribeWebAdminAuthErrorBridge = null;
  });
}
